// Client de l'onglet « Maia » (spec docs/specs/2026-10-09-onglet-maia-design.md § 6). Une réponse = des CODES, jamais un
// texte de technicien (le texte n'existe que pour le superadmin, refusé par le serveur sinon).
import { apiFetch, okOrThrow } from './api';

const base = (ticketId) => `/api/tk/tickets/${encodeURIComponent(ticketId)}/maia`;
const enJson = (method, body) => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export const ETATS = {
  ouverte: 'À répondre', transmise: 'Transmise à William', repondue: 'Répondue', a_executer: 'En attente d’exécution',
  execution: 'En cours d’exécution', faite: 'Posée', partielle: 'Posée en partie', echec: 'Échec', expiree: 'Expirée', annulee: 'Annulée',
};

export const lireOngletMaia = (ticketId) => apiFetch(base(ticketId)).then(okOrThrow);
export const repondreQuestion = (ticketId, questionId, { choix, empreinte, texte }) =>
  apiFetch(`${base(ticketId)}/questions/${encodeURIComponent(questionId)}/reponse`,
    enJson('POST', texte ? { choix, empreinte, texte } : { choix, empreinte })).then(okOrThrow);
export const ecrireAMaia = (ticketId, texte) => apiFetch(`${base(ticketId)}/messages`, enJson('POST', { texte })).then(okOrThrow);
export const lireJournal = (ticketId, sessionId, apres = -1) =>
  apiFetch(`${base(ticketId)}/journal?session=${encodeURIComponent(sessionId)}&apres=${apres}`).then(okOrThrow);
// Une session longue se lit EN ENTIER, page après page (relecture du 10/10 : 2 sessions réelles sur 7 dépassaient la
// page de 200 de la route, et leur fin — conclusions et gestes — restait invisible). Plafond : jamais de boucle sans fin.
export const PAGE_JOURNAL = 200;
export async function lireJournalComplet(ticketId, sessionId, lire = lireJournal, pagesMax = 100) {
  const tout = [];
  let apres = -1;
  for (let i = 0; i < pagesMax; i += 1) {
    const page = (await lire(ticketId, sessionId, apres)).entrees || [];
    tout.push(...page);
    if (page.length < PAGE_JOURNAL) break;
    apres = page[page.length - 1].seq;
  }
  return tout;
}
// `clientId` : entité du sélecteur (30/10/2026), vraie société ou sentinelle d'entité virtuelle — traduite par le serveur.
export const lireMaiaAttend = (clientId = '') => apiFetch(clientId
  ? `/api/maia/attente?client_id=${encodeURIComponent(clientId)}`
  : '/api/maia/attente').then(okOrThrow);
// Pastille de la barre latérale (#20769) : questions à répondre + demandes en cours ou finies non vues.
export const lireMaiaAttendCompte = () => apiFetch('/api/maia/attente/compte').then(okOrThrow);
// Annoncé quand le lecteur vient de voir quelque chose de Maia : la barre latérale se relit sans attendre sa minute.
export const EVENEMENT_MAIA_VU = 'meteris:maia-vu';
export const annoncerMaiaVu = () => { try { window.dispatchEvent(new Event(EVENEMENT_MAIA_VU)); } catch (e) { /* sans fenêtre : rien */ } };
// Maia lancée seule (spec docs/specs/2026-10-12-maia-lancement-automatique-design.md) : état de la demande, synthèse à
// publier, interrupteur et coûts (ces deux derniers : superadmin, c'est le serveur qui décide).
export const DEMANDE_VIVANTE = ['en_attente', 'en_cours'];
export const demandeVivante = (d) => !!d && DEMANDE_VIVANTE.includes(d.etat);
export const lireLancement = () => apiFetch('/api/maia/lancement').then(okOrThrow);
export const ecrireLancement = (corps) => apiFetch('/api/maia/lancement', enJson('PUT', corps)).then(okOrThrow);
export const lireCouts = (mois) => apiFetch(`/api/maia/couts?mois=${encodeURIComponent(mois)}`).then(okOrThrow);
export const annulerDemande = (demandeId) => apiFetch(`/api/maia/demandes/${encodeURIComponent(demandeId)}/annuler`, enJson('POST', {})).then(okOrThrow);
export const synthesePubliee = (ticketId, tacheId) => apiFetch(`${base(ticketId)}/demande/synthese`, enJson('POST', { tache_id: tacheId })).then(okOrThrow);
/** La synthèse devient une tâche PUBLIQUE du ticket, au nom de qui la publie, avec SON temps (route ordinaire des tâches). */
export const creerTacheDeSynthese = (ticketId, texte, minutes, moi) =>
  apiFetch(`/api/tk/tickets/${encodeURIComponent(ticketId)}/tasks`, enJson('POST', {
    title: 'Synthèse de l’intervention', description: texte, status: 'done', time_spent_minutes: minutes, visibility: 'public',
    in_synthesis: true, ...(moi ? { assigned_user_id: moi } : {}),
  })).then(okOrThrow).then((d) => d.task);
export const lireUsageAbonnement = (jours) => apiFetch(`/api/admin/ai-usage/abonnement?days=${jours}`).then(okOrThrow);
/** Un coût simulé, en dollars (c'est l'unité du prix public) : « 1,83 $ » ; inconnu → « — ». */
export const dollars = (x) => (typeof x === 'number' ? `${x.toFixed(2).replace('.', ',')} $` : '—');
// Dossier Maia (spec docs/specs/2026-10-12-dossier-maia-design.md) : le dossier d'un poste, tel que CE lecteur a le droit de
// le voir (le serveur a déjà retiré les tickets qu'il ne lit pas), et les statistiques de recommandation (superadmin).
export const lireDossierPoste = (deviceId) => apiFetch(`/api/maia/dossiers/postes/${encodeURIComponent(deviceId)}`).then(okOrThrow);
export const lireStatsRecommandations = () => apiFetch('/api/maia/statistiques/recommandations').then(okOrThrow);

// Recherche sans accents ni casse (« regle » trouve « Règle »).
export const sansAccents = (t) => String(t == null ? '' : t).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const texteEtape = (e) => sansAccents([e.titre, e.pourquoi, e.resultat, e.deduction,
  ...(e.actions || []).flatMap((a) => [a.libelle, a.script])].filter(Boolean).join(' '));

/** Les étapes d'une section qui répondent à la recherche : toutes si le numéro ou le titre du ticket répond, sinon
 *  celles dont le texte porte TOUS les mots. Recherche vide : toutes. */
export function etapesTrouvees(section, recherche) {
  const mots = sansAccents(recherche).split(/\s+/).filter(Boolean);
  if (!mots.length) return section.etapes;
  const entete = sansAccents(`#${section.ticket.numero} ${section.ticket.numero} ${section.ticket.titre || ''}`);
  if (mots.every((m) => entete.includes(m))) return section.etapes;
  return section.etapes.filter((e) => { const t = texteEtape(e); return mots.every((m) => t.includes(m)); });
}

/** Le fil d'un ticket dans l'ordre où les choses se sont passées : les étapes, puis la question qu'elles ont amenée
 *  (`etapes_jusqua`), puis la suite. `questions` arrive du serveur de la plus récente à la plus ancienne. */
export function filDuTicket(etapes, questions) {
  const fil = [];
  let fait = 0;
  [...(questions || [])].reverse().forEach((q, i) => {
    (etapes || []).filter((e) => e.numero > fait && e.numero <= (q.etapes_jusqua || 0)).forEach((e) => fil.push({ type: 'etape', etape: e }));
    fait = Math.max(fait, q.etapes_jusqua || 0);
    fil.push({ type: 'question', question: q, numero: i + 1 });
  });
  (etapes || []).filter((e) => e.numero > fait).forEach((e) => fil.push({ type: 'etape', etape: e }));
  return fil;
}

/** Étapes posées APRÈS la dernière question du ticket (ou toutes, sans question) : la démarche en cours. */
export const etapesEnCours = (etapes, questions) => {
  const jusqua = Math.max(0, ...(questions || []).map((q) => q.etapes_jusqua || 0));
  return (etapes || []).filter((e) => e.numero > jusqua);
};

export const enCours = (questions) => (questions || []).some((q) => q.etat === 'a_executer' || q.etat === 'execution');
// Ce que la fiche retient de l'onglet : il se montre à un technicien dès qu'il porte une question OU une étape.
// nonVu (#20769) : Maia a produit quelque chose depuis la dernière ouverture de l'onglet par CE lecteur (calculé par le serveur).
export const resumeOnglet = (d) => ({ nb: ((d && d.questions) || []).length + ((d && d.etapes) || []).length + (d && d.demande ? 1 : 0), enAttente: (d && d.en_attente) || 0, nonVu: !!(d && d.non_vu) });
export const marquerOngletVu = (ticketId) => apiFetch(`${base(ticketId)}/vu`, enJson('POST', {})).then(okOrThrow).then((r) => { annoncerMaiaVu(); return r; });
export const ongletMaiaVisible = ({ lecteurInterne, superadmin, nb }) => !!lecteurInterne && (!!superadmin || (nb || 0) > 0);
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
